/**
 * Full-size viewer for the screenshots Claude Code took (`forge_screenshot`
 * frames). Opened from the Screenshots panel or the "new screenshots" notice.
 * Arrow keys step through them; the strip underneath jumps to any one.
 */
import { useEffect } from 'react';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { ForgeScreenshot } from '../types';

export interface ForgeScreenshotViewerProps {
  screenshots: ForgeScreenshot[];
  /** The screenshot shown; null closes the viewer. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

function fileName(path: string): string {
  return path.split('/').pop() || path;
}

export function ForgeScreenshotViewer({
  screenshots,
  index,
  onIndexChange,
  onClose,
}: ForgeScreenshotViewerProps) {
  const open = index !== null && screenshots.length > 0;
  const current = open ? screenshots[Math.min(index, screenshots.length - 1)] : null;
  const position = open ? Math.min(index, screenshots.length - 1) : 0;
  const hasPrevious = position > 0;
  const hasNext = position < screenshots.length - 1;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrevious) onIndexChange(position - 1);
      if (e.key === 'ArrowRight' && hasNext) onIndexChange(position + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, position, hasPrevious, hasNext, onIndexChange]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="forge-screenshot-viewer">
        <DialogHeader>
          <DialogTitle>
            Screenshot {position + 1} of {screenshots.length}
          </DialogTitle>
          <DialogDescription className="truncate" title={current?.path}>
            {current ? fileName(current.path) : ''}
            {current && ` · ${new Date(current.receivedAt).toLocaleTimeString()}`}
          </DialogDescription>
        </DialogHeader>
        {current && (
          <div className="relative flex items-center justify-center bg-muted/40 rounded border">
            <img
              src={current.dataUrl}
              alt={`Screenshot ${current.path}`}
              className="max-h-[70vh] w-auto max-w-full object-contain"
            />
            {hasPrevious && (
              <Button
                size="icon"
                variant="secondary"
                className="absolute left-2 top-1/2 -translate-y-1/2"
                onClick={() => onIndexChange(position - 1)}
                aria-label="Previous screenshot"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            {hasNext && (
              <Button
                size="icon"
                variant="secondary"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                onClick={() => onIndexChange(position + 1)}
                aria-label="Next screenshot"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <div className="flex flex-1 gap-1 overflow-x-auto py-1">
            {screenshots.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onIndexChange(i)}
                className={cn(
                  'h-12 w-16 flex-shrink-0 overflow-hidden rounded border',
                  i === position ? 'ring-2 ring-primary' : 'opacity-70 hover:opacity-100',
                )}
                aria-label={`Show screenshot ${i + 1}`}
                title={s.path}
              >
                <img src={s.dataUrl} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          {current && (
            <Button variant="outline" size="sm" asChild>
              <a href={current.dataUrl} download={fileName(current.path)}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </a>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
